import { campaigns_queue_index_path } from "~/lib/routes"
import { EmailCapacityBreakdown } from "~/react/components/email_accounts_center/email-capacity-breakdown"
import { HealthScoreIndicator } from "~/react/ui/health-score-indicator"
import { Avatar, AvatarFallback } from "~/react/ui/avatar"
import { Tag, TagLabel } from "~/react/ui/tag"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/react/ui/tooltip"
import { Skeleton } from "~/react/ui/skeleton"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { DataTableCellActions } from "~/react/ui/data-table"
import { ActionsCell } from "~/react/components/data-table/cells/actions-cell"
import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"
import {
  getInitials,
  getHealthScore,
  isPendingAccount,
  providerIconMap,
  smtpImapIcon,
  type CapacityData,
  type ColumnDef,
} from "./utils"

type ColumnCallbacks = {
  onViewAccount: (account: EmailAccount) => void
  onRunHealthCheck: (account: EmailAccount) => void
  onDisconnect: (account: EmailAccount) => void
  onCancelRenewal: (account: EmailAccount) => void
  onReconnect: (account: EmailAccount) => void
  onDelete: (account: EmailAccount) => void
}

type ColumnDeps = {
  sharedWorkspace: boolean
  isPremium: boolean
  capacityData: Record<number, CapacityData>
  statusData: Record<string, { status: string }>
  loadingCapacity: Set<number>
}

export function buildDefaultColumns(deps: ColumnDeps, callbacks: ColumnCallbacks): ColumnDef[] {
  const { sharedWorkspace, isPremium, capacityData, statusData, loadingCapacity } = deps
  const { onViewAccount, onRunHealthCheck, onDisconnect, onCancelRenewal, onReconnect, onDelete } = callbacks

  const columns: ColumnDef[] = [
    {
      id: "email",
      sticky: true,
      renderHeader: () => i18n.t("js.email_accounts_center.columns.email_address"),
      renderCell: (account) => (
        <Button
          variant="ghost"
          size="xs"
          className="lead-tag lead-tag--people"
          type="button"
          onClick={() => onViewAccount(account)}
        >
          <img className="lead-tag__avatar p-05" src={providerIconMap[account.provider] || smtpImapIcon} alt="" />
          <div className="lead-tag__name">{account.email}</div>
        </Button>
      ),
    },
    {
      id: "sender_name",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.sender_name"),
      renderCell: (account) => <span className="text-xs">{account.display_name || "-"}</span>,
    },
    {
      id: "status",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.status"),
      renderCell: (account) => {
        if (isPendingAccount(account)) {
          return (
            <Tag variant="warning">
              <TagLabel>{i18n.t("js.email_accounts_center.status.pending", { default: "Pending" })}</TagLabel>
            </Tag>
          )
        }
        const status = statusData[String(account.id)]?.status ?? account.status

        if (status === "loading" || status === undefined) {
          return (
            <Skeleton
              size="freeform"
              style={{ width: "6rem", height: "1.6rem" }}
              aria-label={i18n.t("js.data_table.loading")}
            />
          )
        }

        const getStatusVariant = () => (status === "active" ? "success" : "danger")
        const getStatusLabel = () =>
          status === "active"
            ? i18n.t("js.email_accounts_center.status.active")
            : i18n.t("js.email_accounts_center.status.disconnected")

        return (
          <Tag variant={getStatusVariant()}>
            <TagLabel>{getStatusLabel()}</TagLabel>
          </Tag>
        )
      },
    },
    {
      id: "capacity",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.sends_limit"),
      renderCell: (account) => {
        const isLoading = loadingCapacity.has(account.id)
        const capacity = capacityData[account.id]
        const sent = capacity?.sent ?? 0
        const limit = capacity?.limit ?? 0
        const scheduled = capacity?.scheduled ?? 0

        if (isLoading && !capacity) {
          return (
            <Skeleton
              size="freeform"
              style={{ width: "8rem", height: "1.6rem" }}
              aria-label={i18n.t("js.data_table.loading")}
            />
          )
        }

        return <EmailCapacityBreakdown current={sent} total={limit} secondary={scheduled} showSecondary={true} />
      },
    },
    {
      id: "scheduled",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.scheduled"),
      renderCell: (account) =>
        account.scheduled > 0 ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a className="h-data-table-cell__link" href={campaigns_queue_index_path()}>
                  {i18n.t("js.email_accounts_center.scheduled_count", { count: account.scheduled })}
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <span
                  dangerouslySetInnerHTML={{
                    __html: i18n.t("js.email_accounts_center.scheduled_tooltip_html", {
                      count: account.scheduled,
                      campaigns_count: 1,
                    }),
                  }}
                />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-xs text-body-secondary">
            {i18n.t("js.email_accounts_center.scheduled_count", { count: 0 })}
          </span>
        ),
    },
  ]

  if (isPremium) {
    columns.push({
      id: "health",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.health"),
      renderCell: (account) => {
        const isHealthCheckPending = account.health_check_status === "pending"
        return (
          <>
            <HealthScoreIndicator score={getHealthScore(account.health)} loading={isHealthCheckPending} />
            <DataTableCellActions>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="xs"
                      type="button"
                      iconOnly
                      onClick={() => onRunHealthCheck(account)}
                      disabled={isHealthCheckPending || account.status === "pending"}
                      aria-label={i18n.t("js.email_accounts_center.actions.run_health_check")}
                    >
                      <Icon icon="heart-pulse" size="sm" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{i18n.t("js.email_accounts_center.actions.run_health_check")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </DataTableCellActions>
          </>
        )
      },
      cellProps: {
        style: { width: "18rem", minWidth: "18rem" },
      },
    })
  }

  if (sharedWorkspace) {
    columns.push({
      id: "owner",
      renderHeader: () => i18n.t("js.email_accounts_center.columns.owner"),
      renderCell: (account) => (
        <Avatar size="sm">
          <AvatarFallback>{getInitials(account.owner_name)}</AvatarFallback>
        </Avatar>
      ),
    })
  }

  columns.push({
    id: "actions",
    renderHeader: () => null,
    renderCell: (account) => {
      // Disable all actions for pending accounts (no EmailAccount record yet)
      const isPending = isPendingAccount(account)
      const isHealthCheckPending = account.health_check_status === "pending"
      // Use fetched status data when we have a numeric id (account.id != null handles id 0; isPendingAccount excludes string ids)
      const status = isPendingAccount(account)
        ? account.status
        : (statusData[String(account.id)]?.status ?? account.status)

      const moreActions: {
        label: string
        onClick: () => void
        variant?: "danger"
        disabled?: boolean
      }[] = [
        {
          label: i18n.t("js.email_accounts_center.actions.run_health_check"),
          onClick: () => onRunHealthCheck(account),
          disabled: isPending || isHealthCheckPending,
        },
      ]

      if (status === "active") {
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.disconnect"),
          onClick: () => onDisconnect(account),
          variant: "danger" as const,
          disabled: isPending,
        })

        if (account.managed && !isPending) {
          moreActions.push({
            label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
            onClick: () => onCancelRenewal(account),
            variant: "danger" as const,
          })
        }
      } else {
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.reconnect"),
          onClick: () => onReconnect(account),
          disabled: isPending,
        })
        moreActions.push({
          label: i18n.t("js.email_accounts_center.actions.delete"),
          onClick: () => onDelete(account),
          variant: "danger" as const,
          disabled: isPending,
        })
      }

      return (
        <ActionsCell
          primaryAction={{
            label: i18n.t("js.email_accounts_center.actions.view"),
            onClick: () => onViewAccount(account),
          }}
          moreActions={moreActions}
          moreActionsAriaLabel={i18n.t("js.email_accounts_center.actions.more_actions_aria")}
        />
      )
    },
  })

  return columns
}
